import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";
import { prodApi, fmtEur, fmtPct, fmtDate, fmtKg, errMsg, METHODS, labelOf } from "./prodApi";
import { PButton, PTitle, PCard, PEmpty, Spinner, PBadge } from "./ProdUi";
import ProdResult from "./ProdResult";

export default function ProdSimulations() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [list, setList] = useState(null);
  const [scenarios, setScenarios] = useState([]);
  const [sim, setSim] = useState(null);

  useEffect(() => { if (!id) { prodApi.get("/production/simulations").then((r) => setList(r.data)).catch(() => setList([])); prodApi.get("/production/scenarios").then((r) => setScenarios(r.data)).catch(() => setScenarios([])); } }, [id]);
  useEffect(() => { if (id) { setSim(null); prodApi.get(`/production/simulations/${id}`).then((r) => setSim(r.data)).catch((e) => { toast.error(errMsg(e)); navigate("/produzione/simulazioni"); }); } }, [id, navigate]);

  const remove = async (s) => {
    if (!window.confirm(`Eliminare la simulazione ${s.number}?`)) return;
    try { await prodApi.delete(`/production/simulations/${s.id}`); toast.success("Simulazione eliminata"); if (id) navigate("/produzione/simulazioni"); else setList((l) => l.filter((x) => x.id !== s.id)); } catch (e) { toast.error(errMsg(e)); }
  };

  if (id) {
    if (!sim) return <Spinner text="Caricamento simulazione..." />;
    const i = sim.input || {};
    return (
      <div className="space-y-5" data-testid="prod-simulation-detail">
        <PTitle kicker={`${sim.number} · ${fmtDate(sim.created_at)} · ${sim.created_by}`} title={sim.name}
          right={<>
            <PButton onClick={() => navigate("/produzione/simulazioni")} data-testid="prod-sim-back">← Simulazioni</PButton>
            <PButton variant="primary" onClick={() => navigate(`/produzione/calcolatore?recipe=${sim.recipe_id}`)} data-testid="prod-sim-reopen">Riapri nel calcolatore</PButton>
            <PButton variant="danger" onClick={() => remove(sim)} data-testid="prod-sim-delete"><Trash2 className="w-4 h-4" /></PButton>
          </>}>
          {sim.recipe_name} (v{sim.recipe_version}) · {fmtKg(i.pieces, 0)} pz × {fmtKg(i.avg_weight_kg, 1)} kg a {fmtEur(i.price_per_kg)}/kg · metodo {labelOf(METHODS, sim.method)}{i.supplier_name ? ` · ${i.supplier_name}` : ""}
        </PTitle>
        {sim.notes && <PCard className="text-sm text-slate-300">{sim.notes}</PCard>}
        <ProdResult result={sim.result} />
      </div>
    );
  }

  const removeScenario = async (s) => { if (!window.confirm(`Eliminare lo scenario ${s.number}?`)) return; try { await prodApi.delete(`/production/scenarios/${s.id}`); setScenarios((l) => l.filter((x) => x.id !== s.id)); } catch (e) { toast.error(errMsg(e)); } };

  return (
    <div data-testid="prod-simulations-page">
      <PTitle kicker="Archivio" title="Scenari e simulazioni" right={<PButton variant="primary" onClick={() => navigate("/produzione/calcolatore")} data-testid="prod-sim-new">Nuovo scenario</PButton>}>
        Gli scenari del calcolatore a cascata si riaprono e modificano; le simulazioni del vecchio calcolatore restano consultabili.
      </PTitle>
      <h2 className="font-['Chivo'] font-black text-white text-lg mb-2">Scenari a cascata</h2>
      {scenarios.length === 0 && <p className="text-sm text-slate-500 mb-5" data-testid="prod-scenarios-empty">Nessuno scenario salvato: dal Calcolatore premi «Salva scenario».</p>}
      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-3 mb-8" data-testid="prod-scenarios-list">
        {scenarios.map((s) => (
          <PCard key={s.id} className="flex flex-col gap-2" data-testid={`prod-scenario-card-${s.number}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0"><p className="text-xs text-slate-400">{s.number} · {fmtDate(s.updated_at)} · {s.updated_by}</p><h3 className="font-['Chivo'] font-black text-white leading-tight truncate">{s.name}</h3><p className="text-xs text-slate-400 mt-0.5">{s.input_node_name} · {s.totals?.stages} lavorazioni</p></div>
              <PBadge tone={s.totals?.margin < 0 ? "red" : "green"}>{fmtPct(s.totals?.margin_pct)}</PBadge>
            </div>
            <div className="grid grid-cols-3 gap-2 text-xs mt-1">
              <div><p className="text-slate-500">Input</p><p className="text-white font-bold tabular-nums">{fmtKg(s.totals?.input_kg, 0)} kg</p></div>
              <div><p className="text-slate-500">Fatturato</p><p className="text-white font-bold tabular-nums">{fmtEur(s.totals?.revenue_total)}</p></div>
              <div><p className="text-slate-500">Margine</p><p className={`font-bold tabular-nums ${s.totals?.margin < 0 ? "text-red-400" : "text-emerald-400"}`}>{fmtEur(s.totals?.margin)}</p></div>
            </div>
            <div className="flex gap-2 mt-auto pt-2">
              <PButton size="sm" variant="primary" className="flex-1" onClick={() => navigate(`/produzione/calcolatore?scenario=${s.id}`)} data-testid={`prod-scenario-open-${s.number}`}>Apri nel calcolatore</PButton>
              <PButton size="sm" variant="danger" onClick={() => removeScenario(s)} data-testid={`prod-scenario-delete-${s.number}`}><Trash2 className="w-4 h-4" /></PButton>
            </div>
          </PCard>
        ))}
      </div>
      <h2 className="font-['Chivo'] font-black text-white text-lg mb-2">Simulazioni (vecchio calcolatore)</h2>
      {list === null && <Spinner text="Caricamento..." />}
      {list?.length === 0 && <PEmpty title="Nessuna simulazione" text="Dal Calcolatore puoi salvare uno scenario per confrontarlo in seguito." />}
      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-3">
        {list?.map((s) => (
          <PCard key={s.id} className="flex flex-col gap-2" data-testid={`prod-sim-card-${s.number}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="text-xs text-slate-400">{s.number} · {fmtDate(s.created_at)}</p>
                <h3 className="font-['Chivo'] font-black text-white leading-tight truncate">{s.name}</h3>
                <p className="text-xs text-slate-400 mt-0.5">{s.recipe_name} v{s.recipe_version} · {labelOf(METHODS, s.method)}</p>
              </div>
              <PBadge tone={s.result?.result?.margin < 0 ? "red" : "green"}>{fmtPct(s.result?.result?.margin_pct)}</PBadge>
            </div>
            <div className="grid grid-cols-3 gap-2 text-xs mt-1">
              <div><p className="text-slate-500">Input</p><p className="text-white font-bold tabular-nums">{fmtKg(s.result?.mass?.input_kg, 0)} kg</p></div>
              <div><p className="text-slate-500">Fatturato</p><p className="text-white font-bold tabular-nums">{fmtEur(s.result?.revenue?.sales)}</p></div>
              <div><p className="text-slate-500">Margine</p><p className={`font-bold tabular-nums ${s.result?.result?.margin < 0 ? "text-red-400" : "text-emerald-400"}`}>{fmtEur(s.result?.result?.margin)}</p></div>
            </div>
            <div className="flex gap-2 mt-auto pt-2">
              <Link to={`/produzione/simulazioni/${s.id}`} className="flex-1"><PButton size="sm" className="w-full" data-testid={`prod-sim-open-${s.number}`}>Apri</PButton></Link>
              <PButton size="sm" variant="danger" onClick={() => remove(s)} data-testid={`prod-sim-delete-${s.number}`}><Trash2 className="w-4 h-4" /></PButton>
            </div>
          </PCard>
        ))}
      </div>
    </div>
  );
}
